import { Injectable } from '@angular/core';
import { Line } from './../models/line.model';
import { find, chain, filter, includes, values } from 'lodash';
import { ICartItem, IParamsSearch } from "@app/shared";
import { Subject } from 'rxjs/Subject';
import { incrementStockCombine } from '@app/state/general/app.actions';
import { Store } from '@ngrx/store';

@Injectable({
    providedIn: 'root'
})
export class PanierService {
    itemsSubject = new Subject<Array<ICartItem>>();
    private items: Array<ICartItem> = [];
    private paramsSearch: IParamsSearch;
    constructor(private store: Store<any>) { }

    /**
     * add item in cart
     */
    public add(idRoom: number, moreData: { newTitle: string, surface: string }, line: Line, stockSelected: number, keybed: string, valbed: Array<any>) {
        let oc = line.occupation.split('_');
        let o: ICartItem = {
            idRoom: idRoom,
            idLine: line.id,
            engine: line.engineSelected.alias,
            occupation: line.occupation,
            indexSearch: line.indexSearch,
            adults: parseInt(oc[0]),
            child: parseInt(oc[1]),
            stock: stockSelected,
            keybed: keybed,
            valbed: valbed,
            line: line,
            idOffer: 0,
            newTitle: moreData.newTitle,
            surface: moreData.surface,
        };
        let oldObj = find(this.items, { idRoom: idRoom, idLine: line.id, indexSearch: line.indexSearch });
        if (oldObj) {
            oldObj.stock = stockSelected;
        } else {
            this.items.push(o);
        }
        this.emitItemsSubject();
    }

    /**
     * is Grantie Required
     * @returns boolean
     */
    public isGrantieRequired() {
        let result = filter(this.items, (o: ICartItem) => o.stock > 0).map((o: ICartItem) => o.line.getGarantie());
        return includes(result, true);
    }

    /**
     * clear cart
     */
    public clear(): void {
        this.items = [];
        this.emitItemsSubject();
        //fix bug : after change search last combine add to basket automatique
        this.store.dispatch(new incrementStockCombine({ combineIncremented: {}, indexSearch: '' }));
    }

    /**
     * get items in cart
     * @returns Array<any>
     */
    public getItems(): Array<ICartItem> {
        return this.items;
    }

    /**
     * list items in cart
     * @returns Array<ICartItem>
     */
    public convertToItems(): Array<ICartItem> {
        let results = [];
        this.items.map(o => {
            for (let i = 1; i <= o.stock; i++) {
                let r: ICartItem = {
                    adults: o.adults,
                    child: o.child,
                    idLine: o.idLine,
                    idOffer: o.idOffer,
                    idRoom: o.idRoom,
                    line: o.line,
                    engine: o.engine,
                    occupation: o.occupation,
                    indexSearch: o.indexSearch,
                    stock: 1,
                    keybed: o.keybed,
                    valbed: o.valbed,
                    newTitle: o.newTitle,
                    surface: o.surface,
                };
                results.push(r);
            }
        });
        return results;
    }

    /**
     * get stock panier by idRoom
     * @returns number
     */
    public getStockByRoom(idRoom: number): number {
        let stock = chain(this.items).filter({ idRoom: idRoom }).sumBy('stock').value();
        return stock;
    }
    /**
     * get stock panier by idRoom & line
     * @returns number
     */
    public getStockByRoomLine(idRoom: number, line: Line): number {
        return this.getStockByRoomLineV2(idRoom, line.id, line.occupation);
    }

    /**
     * get stock panier by idRoom & line
     * @returns number
     */
    public getStockByRoomLineV2(idRoom: number, idLine: number, occupation: string): number {
        let stock = chain(this.items).filter({ idRoom: idRoom, idLine: idLine, occupation: occupation }).sumBy('stock').value();
        return stock;
    }

    /**
     * get stock panier by idRoom, line & engine
     * @returns number
     */
    public getStockByRoomLineV3(idRoom: number, idLine: number, engine: string): number {
        let stock = chain(this.items).filter({ idRoom: idRoom, idLine: idLine, engine: engine }).sumBy('stock').value();
        return stock;
    }

    /**
     * get stock panier by idRoom & engine
     * @returns number
     */
    public getStockByRoomByOta(idRoom: number, engine: string): number {
        let stock = chain(this.items).filter({ idRoom: idRoom, engine: engine }).sumBy('stock').value();
        return stock;
    }
    /**
     * get stock panier by indexSearch
     * @returns number
     */
    public getStockByIndex(indexSearch: string): number {
        let stock = chain(this.items).filter({ indexSearch: indexSearch }).sumBy('stock').value();
        return stock;
    }

    /**
     * get nbr Rooms & nbr Adults
     * @returns {rooms: number, adults: number}
     */
    public getNbrRooms(): number {
        return chain(this.items).sumBy('stock').value();
    }

    /**
     * get nbr Adults
     * @returns number
     */
    public getNbrAdults(): number {
        return chain(this.items).filter(o => o.stock > 0).sumBy(o => o.adults * o.stock).value();
    }

    /**
     * get nbr child
     * @returns number
     */
    public getNbrChild(): number {
        return chain(this.items).filter(o => o.stock > 0).sumBy(o => o.child * o.stock).value();
    }

    /**
     * get total cart
     * @param boolean hasPromo price with/out promo
     * @param string field of detailPrice ex : Ttc, HT, Sale...
     * @returns {}
     */
    public getTotal(hasPromo: boolean, field: string = 'Ttc') {
        let r = chain(this.items).filter(o => o.stock > 0).map(o => {
            return { stock: o.stock, price: hasPromo ? o.line.engineSelected.combine.detailPrice : o.line.engineSelected.combine.detailPrice_without_promo };
        }).sumBy(o => o.price[field] * o.stock).value();
        return r;
    }

    /**
     * get taxes details aggregated and grouped by type
     * @param hasPromo boolean
     * @returns { inc: Array<{ name: string, amount: number }>, exc: Array<{ name: string, amount: number }> }
     */
    public getTaxesDetails(hasPromo: boolean): { inc: Array<{ name: string, amount: number }>, exc: Array<{ name: string, amount: number }> } {
        let taxes = { inc: [], exc: [] };
        chain(this.items).filter(o => o.stock > 0).forEach(o => {
            let detailPrice = hasPromo ? o.line.engineSelected.combine.detailPrice : o.line.engineSelected.combine.detailPrice_without_promo;
            let taxeContainer = detailPrice.details || detailPrice.Taxes;

            if (taxeContainer && typeof taxeContainer === 'object' && !Array.isArray(taxeContainer)) {
                if (taxeContainer.exc) {
                    values(taxeContainer.exc).forEach(t => {
                        if (t && t.name && t.montant !== undefined) {
                            let tax = find(taxes.exc, { name: t.name });
                            if (tax) {
                                tax.amount += t.montant * o.stock;
                            } else {
                                taxes.exc.push({ name: t.name, amount: t.montant * o.stock });
                            }
                        }
                    });
                }
                if (taxeContainer.inc) {
                    values(taxeContainer.inc).forEach(t => {
                        if (t && t.name && t.montant !== undefined) {
                            let tax = find(taxes.inc, { name: t.name });
                            if (tax) {
                                tax.amount += t.montant * o.stock;
                            } else {
                                taxes.inc.push({ name: t.name, amount: t.montant * o.stock });
                            }
                        }
                    });
                }
            }
        }).value();
        return taxes;
    }

    /**
     * get stock panier by idRoom & line
     * @param idRoom        number idRoom
     * @param line          Line   Object Line
     * @param idOffer       Number Id Offer
     */
    public changeOffer(idRoom: number, line: Line, idOffer: number): void {
        let obj = find(this.items, { idRoom: idRoom, idLine: line.id, indexSearch: line.indexSearch });
        if (obj) {
            obj.idOffer = idOffer;
        }
    }

    /**
     * get idOffre by indexSerach
     */
    public getOfferId(line: Line): number {
        let obj = find(this.items, { indexSearch: line.indexSearch });
        if (obj) {
            return obj.idOffer;
        }
        return 0;
    }

    public emitItemsSubject() {
        this.itemsSubject.next(this.items.slice());
    }

    setParamsSearch(paramsSearch: IParamsSearch): PanierService {
        this.paramsSearch = paramsSearch;
        return this;
    }

    getParamsSearch(): IParamsSearch {
        return this.paramsSearch;
    }
}
